import type { StoryboardPipeline } from '@/store/slices/creativeJobs';

import { ROUTE } from '../../routes';

/**
 * The Video Ads deep-link view named by a pathname: the library grid, a "create new", or a specific saved ad.
 * A pure function (no react-router import) so it is unit-testable in the node jest env AND can seed the page's
 * initial tab/view state at mount. The Marketing Assets root and its video routes are ONE page instance (see
 * PageTransition and the router), so a live page also re-derives its view from each new pathname (viewForUrl).
 */
export type VideoView = { kind: 'library' } | { kind: 'new' } | { kind: 'edit'; id: string };

/** A Video Ads URL resolved: which video tab it belongs to (named by the pipeline that tab creates: `beats` is
 *  Video Ads, `single_clip` is Video Ads v2.0) and the view within it. */
export type VideoRoute = { pipeline: StoryboardPipeline; view: VideoView };

/** The URL builders of one video tab, so a library navigates within its own tab without branching per call. */
export type VideoTabRoutes = { library: () => string; create: () => string; edit: (id: string) => string };

const TAB_ROUTES: Record<StoryboardPipeline, VideoTabRoutes> = {
  beats: { library: ROUTE.creativesVideos, create: ROUTE.creativesVideoNew, edit: ROUTE.creativesVideo },
  single_clip: { library: ROUTE.creativesVideosV2, create: ROUTE.creativesVideoV2New, edit: ROUTE.creativesVideoV2 },
};

/** The route builders of the video tab that creates `pipeline` ads. */
export const videoTabRoutes = (pipeline: StoryboardPipeline): VideoTabRoutes => TAB_ROUTES[pipeline];

/** The view a (trailing-slash-trimmed) path names under one tab's library `base`, or null when it is not under
 *  it. Matches `base` exactly or `base/…`, so `/videos-v2` is never read as a v1 `/videos/:id`. Shared with the
 *  banner-kit and mini-game tabs (`asset-routes.ts`), which use the same library / new / edit shape. */
export const viewUnder = (base: string, path: string): VideoView | null => {
  if (path === base) return { kind: 'library' };
  if (!path.startsWith(`${base}/`)) return null;
  const rest = path.slice(base.length + 1);
  if (rest === 'new') return { kind: 'new' };
  if (rest === '' || rest.includes('/')) return null;
  try {
    return { kind: 'edit', id: decodeURIComponent(rest) };
  } catch {
    return null;
  }
};

/** Parse a pathname into its {@link VideoRoute} (tab + view), or null when it is not a Video Ads URL (under
 *  {@link ROUTE.creativesVideos} or {@link ROUTE.creativesVideosV2}, i.e. Marketing Assets). Old
 *  `/account/creatives/videos` links never reach here: the router redirects them to the Marketing Assets address
 *  first. */
export const videoRouteFromPath = (pathname: string): VideoRoute | null => {
  const path = pathname.replace(/\/+$/, '');
  for (const pipeline of ['beats', 'single_clip'] as const) {
    const view = viewUnder(TAB_ROUTES[pipeline].library(), path);
    if (view) return { pipeline, view };
  }
  return null;
};

/** The {@link VideoView} of a Video Ads URL in EITHER video tab, or null when it is not one. For callers that only
 *  ask "is this a Video Ads page / editor", e.g. the page transition treating the library and its modal editor as
 *  one page. */
export const videoViewFromPath = (pathname: string): VideoView | null => videoRouteFromPath(pathname)?.view ?? null;

/** The view a Marketing Assets page shows for its URL: a matching deep-link URL (a Video Ads one, or a banner-kit /
 *  mini-game one) names it; any other Marketing Assets route closes the editor or new-ad flow (browser Back from an
 *  ad lands there, and the page is the same instance, so nothing remounts to close it) and otherwise keeps the
 *  current view object as is (no re-render). */
export const viewForUrl = (route: { view: VideoView } | null, current: VideoView): VideoView =>
  route ? route.view : current.kind === 'library' ? current : { kind: 'library' };

/** Whether a Video Ads editor location is in ADVANCED (immersive canvas) mode, vs the DEFAULT easy modal. The
 *  mode rides on `?mode=advanced` so both presentations share the same /videos/:id URL (deep-linkable). */
export const isAdvancedMode = (search: string): boolean => new URLSearchParams(search).get('mode') === 'advanced';

/** True when a location is a Video Ads editor (new/edit) in ADVANCED mode — the ONLY case that drives the
 *  immersive full-screen layout. Easy mode (the default) is a normal modal over the library, never immersive. The
 *  advanced canvas edits v1 beats, so only the Video Ads (v1) tab has it: Video Ads v2.0 is never immersive. */
export const isAdvancedVideoEditor = (pathname: string, search: string): boolean => {
  const r = videoRouteFromPath(pathname);
  return r?.pipeline === 'beats' && (r.view.kind === 'edit' || r.view.kind === 'new') && isAdvancedMode(search);
};
